import { getKoulutusmuodot, localizeRouteKey } from "@/utils/common";
import { AppBar, Toolbar, useMediaQuery } from "@mui/material";
import { Breadcrumbs } from "react-breadcrumbs-dynamic";
import { NavLink, useLocation } from "react-router-dom";
import { LanguageSwitcher } from "@/modules/i18n/index";
import { Fragment, useEffect, useState } from "react";
import { IconButton } from "@mui/material";
import { AppRoute } from "@/routes/index";
import { COLORS } from "@/modules/styles";
import { useIntl } from "react-intl";
import { map, values } from "ramda";

import logo_fi from "@/static/images/oiva-logo-fi-tekstilla.svg";
import logo_sv from "@/static/images/oiva-logo-sv-tekstilla.svg";
import AuthenticationLink from "./AuthenticationLink";
import SideNavigation from "../SideNavigation";
import oiva_logo from "@/static/images/oiva-logo.svg";
import makeStyles from "@mui/styles/makeStyles";
import MenuIcon from "@mui/icons-material/Menu";
import common from "@/i18n/definitions/common";
import MobileMenu from "./MobileMenu";
import Navigation from "@/scenes/Navigation";
import PropTypes from "prop-types";
import { setLocale } from "@/services/lomakkeet/i18n-config";

export const MEDIA_QUERIES = {
  MOBILE: "only screen and (min-width: 360px) and (max-width: 767px)",
  TABLET: "only screen and (min-width: 768px) and (max-width: 1023px)",
  TABLET_MIN: "only screen and (min-width: 768px)",
  DESKTOP_NORMAL: "only screen and (min-width: 1024px) and (max-width: 1279px)",
  DESKTOP_LARGE: "only screen and (min-width: 1280px)"
};

const useStyles = makeStyles(theme => ({
  margin: {
    margin: theme.spacing(1)
  },
  extendedIcon: {
    color: "#ffffff",
    marginRight: theme.spacing(1)
  }
}));

const Header = ({ localesByLang, authenticationLink }) => {
  const intl = useIntl();
  const { pathname } = useLocation();
  const { formatMessage, locale } = intl;
  const classes = useStyles();

  const isBreadcrumbVisible =
    pathname !== localizeRouteKey(locale, AppRoute.Home, intl);

  const breakpointTabletMin = useMediaQuery(MEDIA_QUERIES.TABLET_MIN);

  const breakpointDesktopLarge = useMediaQuery(MEDIA_QUERIES.DESKTOP_LARGE);

  const mediumSizedScreen = breakpointTabletMin && !breakpointDesktopLarge;

  const [isMobileMenuVisible, setIsMobileMenuVisible] = useState(false);

  const toggleMobileMenu = () => setIsMobileMenuVisible(!isMobileMenuVisible);

  const koulutusmuodot = getKoulutusmuodot(intl);

  useEffect(() => {
    setLocale(locale);
  }, [locale]);

  return (
    <div className="sticky top-0 w-full z-10">
      {breakpointDesktopLarge && (
        <AppBar elevation={0} position="static">
          <Toolbar className="bg-green-500 px-5 justify-between overflow-hidden">
            <NavLink
              to={localizeRouteKey(locale, AppRoute.Home, intl)}
              className="flex items-center no-underline text-white hover:text-gray-100 pr-10">
              <img
                alt={`${formatMessage(
                  common.opetusJaKulttuuriministerio
                )} logo`}
                src={locale === "sv" ? logo_sv : logo_fi}
                className="max-w-sm"
              />
            </NavLink>
            <div id="navigation-level-1">
              <Navigation
                level={1}
                localesByLang={localesByLang}
                routes={[
                  {
                    key: "JarjestamisJaYllapitamisluvat",
                    route: AppRoute.JarjestamisJaYllapitamisluvat,
                    routes: values(
                      map(koulutusmuoto => {
                        return {
                          key: koulutusmuoto.pascalCase,
                          params: {
                            koulutusmuoto: koulutusmuoto.kebabCase
                          },
                          title: koulutusmuoto.paasivunOtsikko
                        };
                      }, koulutusmuodot)
                    ),
                    titleKey: "common.jarjestamisJaYllapitamisluvat"
                  },
                  {
                    key: "Tilastot",
                    route: AppRoute.Tilastot,
                    titleKey: "common.statistics"
                  }
                ]}
              />
            </div>
            <div className="flex-1 flex justify-end items-center">
              {!!authenticationLink && (
                <AuthenticationLink authenticationLink={authenticationLink} />
              )}
              <LanguageSwitcher ulClasses="border-l border-opacity-25" />
            </div>
          </Toolbar>
        </AppBar>
      )}

      {!breakpointDesktopLarge && (
        <Fragment>
          <SideNavigation
            isVisible={isMobileMenuVisible}
            setIsMobileMenuVisible={setIsMobileMenuVisible}>
            <MobileMenu
              onCloseMenu={toggleMobileMenu}
              authenticationLink={authenticationLink}
            />
          </SideNavigation>
          <AppBar className="bg-green-500" elevation={0} position="static">
            <Toolbar className="justify-start overflow-hidden">
              <IconButton
                aria-label="open-main-menu"
                color="inherit"
                onClick={toggleMobileMenu}
                className={classes.margin}
                size="large">
                <MenuIcon />
              </IconButton>

              <NavLink
                to={localizeRouteKey(locale, AppRoute.Home, intl)}
                className="flex items-center no-underline text-white hover:text-gray-100 mr-16">
                {mediumSizedScreen ? (
                  <img
                    alt={`${formatMessage(
                      common.opetusJaKulttuuriministerio
                    )} logo`}
                    src={locale === "sv" ? logo_sv : logo_fi}
                    className="max-w-sm"
                  />
                ) : (
                  <img
                    alt={`${formatMessage(
                      common.opetusJaKulttuuriministerio
                    )} logo`}
                    src={oiva_logo}
                    className="max-w-sm"
                  />
                )}
              </NavLink>
              <div className="flex-1 flex justify-end items-center">
                {!!authenticationLink && (
                  <AuthenticationLink
                    authenticationLink={authenticationLink}
                    navLinkClasses="leading-snug"
                  />
                )}
                {mediumSizedScreen ? (
                  <LanguageSwitcher
                    localesByLang={localesByLang}
                    ulClasses="border-l border-opacity-25"
                  />
                ) : (
                  ""
                )}
              </div>
            </Toolbar>
          </AppBar>
        </Fragment>
      )}
      {isBreadcrumbVisible && (
        <nav
          tabIndex={0}
          className="breadcumbs-nav py-2 border-b pl-8 text-sm t-[64rem] bg-white"
          aria-label={formatMessage(common.breadCrumbs)}>
          <Breadcrumbs
            separator={<span> / </span>}
            item={NavLink}
            finalItem={"b"}
            finalProps={{
              style: {
                fontWeight: 400,
                color: COLORS.BLACK
              }
            }}
          />
        </nav>
      )}
    </div>
  );
};

Header.propTypes = {
  authenticationLink: PropTypes.object,
  inFinnish: PropTypes.string,
  inSwedish: PropTypes.string,
  isAuthenticated: PropTypes.bool,
  locale: PropTypes.string,
  localesByLang: PropTypes.object,
  logIn: PropTypes.string,
  logo: PropTypes.object,
  onLoginButtonClick: PropTypes.func,
  onMenuClick: PropTypes.func,
  shortDescription: PropTypes.object,
  template: PropTypes.string,
  languageSelectionAriaLabel: PropTypes.string
};

export default Header;
